import Observer from '@researchgate/react-intersection-observer';
import React, { useState, forwardRef } from 'react';
import { Tracker } from '@udemy/event-tracking';
import { useFeatures } from '@udemy/react-experimentation-provider';
import { serverOrClient, noop } from '@udemy/shared-utils';
import { ExperimentImpressionEvent } from '../events';
import styles from "./prefetching-wrapper.module.css";

// https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection

const VISIBILITY_THRESHOLD = 0.25;
const PrefetchInteractionComponent = /*#__PURE__*/forwardRef((props, ref) => {
  const handleChange = (event, unobserve) => {
    if (event.isIntersecting) {
      unobserve();
      props.onView();
    }
  };
  const handleHover = () => {
    props.onHover();
  };
  return /*#__PURE__*/React.createElement(Observer, {
    threshold: VISIBILITY_THRESHOLD,
    onChange: handleChange
  }, /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: styles['prefetching-wrapper'],
    onMouseEnter: handleHover
  }, props.children));
});
const OnHoverPrefetchComponent = /*#__PURE__*/forwardRef((props, ref) => {
  const [hasHovered, setHasHovered] = useState(false);
  return /*#__PURE__*/React.createElement(PrefetchInteractionComponent, {
    ref: ref,
    onHover: () => {
      var _serverOrClient$globa;
      if ((_serverOrClient$globa = serverOrClient.global) !== null && _serverOrClient$globa !== void 0 && _serverOrClient$globa.requestIdleCallback) {
        serverOrClient.global.requestIdleCallback(() => {
          if (!hasHovered) {
            setHasHovered(true);
          }
        }, {
          timeout: 2000
        });
      } else {
        if (!hasHovered) {
          setHasHovered(true);
        }
      }
    },
    onView: noop
  }, hasHovered ? /*#__PURE__*/React.createElement("link", {
    "data-testid": "hover-prefetch",
    rel: "prefetch",
    href: props.url,
    as: "document"
  }) : null, props.children);
});
const OnViewPrefetchComponent = /*#__PURE__*/forwardRef((props, ref) => {
  const [hasViewed, setHasViewed] = useState(false);
  return /*#__PURE__*/React.createElement(PrefetchInteractionComponent, {
    ref: ref,
    onHover: noop,
    onView: () => {
      var _serverOrClient$globa2;
      if ((_serverOrClient$globa2 = serverOrClient.global) !== null && _serverOrClient$globa2 !== void 0 && _serverOrClient$globa2.requestIdleCallback) {
        serverOrClient.global.requestIdleCallback(() => {
          if (!hasViewed) {
            setHasViewed(true);
          }
        }, {
          timeout: 2000
        });
      } else {
        if (!hasViewed) {
          setHasViewed(true);
        }
      }
    }
  }, hasViewed ? /*#__PURE__*/React.createElement("link", {
    "data-testid": "view-prefetch",
    rel: "prefetch",
    href: props.url,
    as: "document"
  }) : null, props.children);
});
export let FetchBehaviour = /*#__PURE__*/function (FetchBehaviour) {
  FetchBehaviour["HOVER"] = "HOVER";
  FetchBehaviour["VIEW"] = "VIEW";
  return FetchBehaviour;
}({});
export const PrefetchingWrapper = /*#__PURE__*/forwardRef((props, ref) => {
  var _prefetchVariantAssig, _serverOrClient$globa3;
  const prefetchVariantAssignment = useFeatures(['course_card_prefetch']).featureVariantAssignments.get('course_card_prefetch');
  const [hasSentImpression, setHasSentImpression] = useState(false);
  const publishExperimentImpression = () => {
    if (!hasSentImpression) {
      if (prefetchVariantAssignment) {
        Tracker.publishEvent(new ExperimentImpressionEvent(prefetchVariantAssignment.experimentIds, prefetchVariantAssignment.isLocalDefault));
        setHasSentImpression(true);
      }
    }
  };
  const fetchBehaviour = prefetchVariantAssignment === null || prefetchVariantAssignment === void 0 || (_prefetchVariantAssig = prefetchVariantAssignment.configuration) === null || _prefetchVariantAssig === void 0 ? void 0 : _prefetchVariantAssig.prefetchBehaviour;
  const connection = (_serverOrClient$globa3 = serverOrClient.global) === null || _serverOrClient$globa3 === void 0 || (_serverOrClient$globa3 = _serverOrClient$globa3.navigator) === null || _serverOrClient$globa3 === void 0 ? void 0 : _serverOrClient$globa3.connection;

  // Do not prefetch if the user has indicated they are trying to save data, if the url to prefetch does not exist,
  // or if the user is on a poor connection.
  // Also do some type shenanigans because TS DOM types don't include the saveData property:
  // https://stackoverflow.com/questions/69673842/typescript-networkinformation-type-does-not-contain-all-properties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clientSavesData = !!(connection !== null && connection !== void 0 && connection.saveData);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clientOnBadConnection = /2g/.test(connection === null || connection === void 0 ? void 0 : connection.effectiveType);
  const skipPrefetch = clientSavesData || clientOnBadConnection || !props.url;
  if (!skipPrefetch) {
    switch (fetchBehaviour) {
      case FetchBehaviour.HOVER:
        {
          return /*#__PURE__*/React.createElement(OnHoverPrefetchComponent, {
            url: props.url,
            ref: ref,
            publishExperimentImpression: publishExperimentImpression
          }, props.children);
        }
      case FetchBehaviour.VIEW:
        {
          return /*#__PURE__*/React.createElement(OnViewPrefetchComponent, {
            url: props.url,
            ref: ref,
            publishExperimentImpression: publishExperimentImpression
          }, props.children);
        }
      default:
        {
          return /*#__PURE__*/React.createElement(PrefetchInteractionComponent, {
            ref: ref,
            onView: noop,
            onHover: noop
          }, props.children);
        }
    }
  }
  return /*#__PURE__*/React.createElement(PrefetchInteractionComponent, {
    ref: ref,
    onView: noop,
    onHover: noop
  }, props.children);
});
//# sourceMappingURL=prefetching-wrapper.react-component.js.map